import { selectCurrentReportDate } from '@components/Dashboard/SalesAttribution/salesAttributionSlice';
import { useAppSelector } from '@utils/hooks';
import {
  useGetCampaignAnalyticsReportsQuery,
  useUpdateCampaignAnalyticsReportMutation,
} from '../slice/campaignAnalyticsSlice';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useParams } from 'react-router-dom';

export const useCurrentLatestCampaignAnalyticsReport = () => {
  const { id: campaignIdFromParams } = useParams<{ id: string }>();

  return useLatestCampaignAnalyticsReport(campaignIdFromParams);
};

export const useLatestCampaignAnalyticsReport = (
  campaignId: string | number | undefined
) => {
  const selectedReportDate = useAppSelector(selectCurrentReportDate);
  const { reports, error, isLoading } = useCampaignAnalyticsReports(campaignId);

  if (!reports || !selectedReportDate)
    return { report: null, error: undefined, isLoading };

  return {
    report: reports
      .filter(
        report =>
          report.status === 'approved' &&
          Date.parse(report.reportDate) <= Date.parse(selectedReportDate)
      )
      .sort((a, b) => Date.parse(b.reportDate) - Date.parse(a.reportDate))[0],
    error,
    isLoading,
  };
};

export const useCampaignAnalyticsReport = (
  id: number | string | undefined,
  campaignId: number | string | undefined,
  showExcludedLocations?: boolean
) => {
  const { reports, error, isLoading } = useCampaignAnalyticsReports(
    campaignId,
    showExcludedLocations
  );
  const [updateReport, updateReportResult] =
    useUpdateCampaignAnalyticsReportMutation();

  if (!reports || !id) return { report: null, error: undefined, isLoading };

  return {
    report: reports.find(report => report.id === Number(id)),
    error,
    isLoading,
    updateReport,
    updateReportResult,
  };
};

export const useCampaignAnalyticsReports = (
  campaignId: number | string | undefined,
  showExcludedLocations?: boolean
) => {
  const { data, error, isFetching } = useGetCampaignAnalyticsReportsQuery(
    campaignId ? { campaignId, showExcludedLocations } : skipToken
  );

  return { reports: data, error, isLoading: isFetching };
};
